// Lib
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { debounce, isEqual } from 'lodash';
import classNames from 'classnames';
import { createStructuredSelector } from 'reselect';

// Utils
import { getColorToPersist } from '../../../../../../common/colors/colorHelper';
import { getFirstSelectedSecondaryColor, getSupportColorBar } from '../colorUtils';
import setTemporarySelectedElementsColorStyles from '../setTemporarySelectedElementsColorStyles';
import setTemporarySelectedElementsBackgroundColorStyles from '../setTemporarySelectedElementsBackgroundColorStyles';
import { isSketch } from '../../../../../../common/elements/utils/elementTypeUtils';
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import {
    regenerateColorCombo,
    updateAppStateUsedColorCombos,
    updateElementUsedColorCombos,
} from '../../../../../element/board/boardActions';
import { isEmpty } from '../../../../../../common/utils/immutableHelper';
import { elementAllowsColorCombos } from '../../../../../element/board/colorComboPaletteUtils';

// Selectors
import {
    getCurrentBoard,
    getCurrentBoardColorComboList,
    getCurrentSuggestedColors,
    getCurrentUsedColorCombos,
} from '../../../../../element/board/boardSelector';
import currentBoardSuggestedColorsSelector from '../currentBoardSuggestedColorsSelector';

// Actions
import { closePopup } from '../../../../../components/popupPanel/popupActions';
import {
    updateSelectedElementsBackgroundColors,
    updateSelectedElementsColors,
    updateSelectedElementsSecondaryColors,
} from '../colorActions';

// Components
import ToolbarPopup from '../../../toolbarPopup/ToolbarPopup';
import ColorPopupContent from './ColorPopupContent';
import CustomColorInput from '../CustomColorInput';

// Hooks
import useActualColorPopupMode from './useActualColorPopupMode';

// Constants
import { COLOR_POPUP_MODE } from './colorPopupConstants';
import { BACKGROUND_COLORS, COLORS, SKETCH_BACKGROUND_COLORS } from '../../../../../../common/colors/colorConstants';
import { SUGGESTED_COLORS_UPDATE } from '../../../../../element/board/colorComboPaletteActionTypes';

// Styles
import './ColorPopup.scss';

const COLORS_ARRAY = Object.values(COLORS);
const BACKGROUND_COLORS_ARRAY = Object.values(BACKGROUND_COLORS);
const SKETCH_BACKGROUND_COLORS_ARRAY = Object.values(SKETCH_BACKGROUND_COLORS);

const getDefaultColors = ({ colorPopupMode, elementType }) => {
    if (colorPopupMode !== COLOR_POPUP_MODE.BACKGROUND) return COLORS_ARRAY;

    return isSketch(elementType) ? SKETCH_BACKGROUND_COLORS_ARRAY : BACKGROUND_COLORS_ARRAY;
};

const colorPopupSelector = createStructuredSelector({
    currentBoard: getCurrentBoard,
    colorComboList: getCurrentBoardColorComboList,
    suggestedColors: currentBoardSuggestedColorsSelector,
    storedSuggestedColors: getCurrentSuggestedColors,
    storedUsedColorCombos: getCurrentUsedColorCombos,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
    closeColorPopup: () => dispatch(closePopup(ownProps.popupId)),
    setTempColor: setTemporarySelectedElementsColorStyles,
    setTempBackgroundColor: setTemporarySelectedElementsBackgroundColorStyles,
    setColor: (colorValue, secondaryColorValue) => {
        const color = getColorToPersist(colorValue);
        const secondaryColor = getColorToPersist(secondaryColorValue);
        dispatch(updateSelectedElementsColors({ color, secondaryColor }));
    },
    setSecondaryColor: (colorValue) => {
        const color = getColorToPersist(colorValue);
        dispatch(updateSelectedElementsSecondaryColors({ color }));
    },
    setBackgroundColor: (colorValue) => {
        const color = getColorToPersist(colorValue);
        dispatch(updateSelectedElementsBackgroundColors(color));
    },
    setAppStateSuggestedColors: (suggestedColors) =>
        dispatch({
            type: SUGGESTED_COLORS_UPDATE,
            suggestedColors,
        }),
    setNewColorComboList: (id) => dispatch(regenerateColorCombo({ id })),
    setAppStateUsedColorCombos: (id) => dispatch(updateAppStateUsedColorCombos({ id })),
    setElementUsedColorCombos: (id) => dispatch(updateElementUsedColorCombos({ id })),
});

const ColorPopup = (props) => {
    const {
        isPopupOpen,
        closeColorPopup,

        popupId,
        buttonSelector,

        selectedElements,
        elementType,

        selectedColor,
        selectedColorHex,
        selectedBackgroundColor,
        selectedBackgroundColorHex,

        setTempColor,
        setColor,
        setSecondaryColor,
        setBackgroundColor,
        setTempBackgroundColor,
        setElementUsedColorCombos,
        setAppStateUsedColorCombos,
        storedUsedColorCombos,

        currentBoard,
        colorComboList,
        setNewColorComboList,
        suggestedColors,
        setAppStateSuggestedColors,
        storedSuggestedColors,
        supportBackgroundColor,

        customDefaultColors,
        customColorHandler,
        customTempColorHandler,
    } = props;

    const [colorPopupMode, setColorPopupMode] = useState(COLOR_POPUP_MODE.BACKGROUND);
    const customColorInputRef = useRef();
    const currentBoardId = getElementId(currentBoard);

    const supportColorBar = getSupportColorBar(selectedElements);
    const isBackgroundMode = colorPopupMode === COLOR_POPUP_MODE.BACKGROUND && supportBackgroundColor;

    const supportColorCombos = !!colorComboList && elementAllowsColorCombos(selectedElements.first());
    const secondaryColor = getFirstSelectedSecondaryColor(selectedElements);
    const updateColorCombos = async () => {
        const elementUsedColorCombos = await setElementUsedColorCombos(currentBoardId);
        const hasUsedColorCombosChanged = !isEqual(elementUsedColorCombos, storedUsedColorCombos);
        const hasSuggestedColorsChanged = !isEqual(suggestedColors, storedSuggestedColors);
        if (isEmpty(colorComboList) || hasSuggestedColorsChanged || hasUsedColorCombosChanged) {
            setAppStateSuggestedColors([...suggestedColors]);
            setAppStateUsedColorCombos(currentBoardId);
            setNewColorComboList(currentBoardId);
        }
    };

    useEffect(() => {
        if (elementAllowsColorCombos(selectedElements.first())) updateColorCombos();
    }, []);

    const actualPopupMode = useActualColorPopupMode({
        isPopupOpen,
        isBackgroundMode,

        supportColorBar,

        selectedColor,
        selectedBackgroundColor,

        setColorPopupMode,
    });

    const defaultColors = getDefaultColors({
        colorPopupMode: actualPopupMode,
        elementType,
    });

    const setColorHandler = isBackgroundMode ? setBackgroundColor : setColor;

    const handleTempColor = (color) => {
        // if there is a secondary color while using the color picker, set it to null on the first input.
        // Needs to happen before setTempColor so that the css for the icon color gets set to black/white
        if (supportColorCombos && secondaryColor) setSecondaryColor(null);
        setTempColor(color);
    };
    const setTempColorHandler = isBackgroundMode ? setTempBackgroundColor : handleTempColor;

    const popupColor = isBackgroundMode ? selectedBackgroundColor : selectedColor;

    const popupColorHex = isBackgroundMode ? selectedBackgroundColorHex : selectedColorHex;

    const openCustomColorPicker = useCallback(() => {
        if (customColorInputRef && customColorInputRef.current) {
            customColorInputRef.current.open();
        }
    }, [customColorInputRef]);

    return (
        <div className={classNames('ColorPopup', { 'is-open': isPopupOpen })}>
            <CustomColorInput
                className="custom-color-picker-input"
                ref={customColorInputRef}
                initialColor={popupColorHex}
                setTempColor={customTempColorHandler || setTempColorHandler}
                setColor={customColorHandler || setColorHandler}
                onBlur={debounce(closeColorPopup, 200)}
            />
            <ToolbarPopup
                popupId={popupId}
                className="color-popup"
                buttonSelector={buttonSelector}
                closePopup={closeColorPopup}
            >
                <ColorPopupContent
                    {...props}
                    defaultColors={customDefaultColors || defaultColors}
                    supportColorBar={supportColorBar}
                    setColor={customColorHandler || setColorHandler}
                    selectedColor={popupColor}
                    openCustomColorPicker={openCustomColorPicker}
                    colorPopupMode={actualPopupMode}
                    setColorPopupMode={setColorPopupMode}
                    colorComboList={colorComboList}
                    supportColorCombos={supportColorCombos}
                    suggestedColors={suggestedColors}
                />
            </ToolbarPopup>
        </div>
    );
};

ColorPopup.propTypes = {
    selectedElements: PropTypes.object,
    elementType: PropTypes.string,

    setTempColor: PropTypes.func.isRequired,
    setColor: PropTypes.func.isRequired,
    setSecondaryColor: PropTypes.func,
    setBackgroundColor: PropTypes.func,
    setTempBackgroundColor: PropTypes.func,

    closeColorPopup: PropTypes.func,
    selectedColor: PropTypes.string,
    selectedColorHex: PropTypes.string,
    selectedBackgroundColor: PropTypes.string,
    selectedBackgroundColorHex: PropTypes.string,

    dispatchAddColorHistoryEntry: PropTypes.func,
    currentUserId: PropTypes.string,
    isPopupOpen: PropTypes.bool,
    supportBackgroundColor: PropTypes.bool,

    colorComboList: PropTypes.object,
    currentBoard: PropTypes.object,
    suggestedColors: PropTypes.array,
    setAppStateSuggestedColors: PropTypes.func,
    storedSuggestedColors: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    storedUsedColorCombos: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    setElementUsedColorCombos: PropTypes.func,
    setNewColorComboList: PropTypes.func,
    setAppStateUsedColorCombos: PropTypes.func,

    popupId: PropTypes.string.isRequired,
    buttonSelector: PropTypes.string,

    customDefaultColors: PropTypes.array,
    customColorHandler: PropTypes.func,
    customTempColorHandler: PropTypes.func,
};

export default connect(colorPopupSelector, mapDispatchToProps)(ColorPopup);
